/* RL avant-première offer selector — PDP tier picker.

   This component owns none of the variant state. Selecting a row checks the
   matching native <variant-selects> radio and dispatches `change`, so the
   theme updates the price block, the CTA label and the sold-out state itself.
   Here we only paint the rows, rebuild the summary line and run the ARIA
   radiogroup keyboard pattern. */
(function () {
  'use strict';

  function initSelector(root) {
    var list = root.querySelector('.rl-avp__list');
    if (!list) return;

    var opts = Array.prototype.slice.call(root.querySelectorAll('[data-rl-avp-opt]'));
    if (!opts.length) return;

    var config = readConfig(root);
    var summaryEl = root.querySelector('[data-rl-avp-sum]');

    var optionName = (root.dataset.optionName || '').toLowerCase().trim();

    // Resolved on every use, never cached: the theme replaces the whole
    // <variant-selects> element after each variant change, so a reference
    // taken at load would point at a detached node from the second click on.
    function getPicker() {
      return document.querySelector('variant-selects');
    }

    // The clipped picker sits in an aria-hidden subtree, so its controls must
    // leave the tab order — otherwise the keyboard stops on an invisible radio
    // before reaching this selector. Re-applied after every re-render, since
    // the fresh markup comes back without our tabindex.
    function silenceHiddenPicker() {
      var hidden = document.querySelector('.rl-avp-hidden-picker');
      if (!hidden) return;
      hidden.querySelectorAll('input, select, button, a[href]').forEach(function (el) {
        el.setAttribute('tabindex', '-1');
      });
    }

    silenceHiddenPicker();

    // The theme re-renders the picker asynchronously, so a one-off call after
    // the change event can land before the fresh markup exists. Watch the
    // wrapper instead and re-apply whenever its contents are swapped.
    var hiddenWrapper = document.querySelector('.rl-avp-hidden-picker');
    if (hiddenWrapper && window.MutationObserver) {
      new MutationObserver(silenceHiddenPicker).observe(hiddenWrapper, { childList: true, subtree: true });
    }

    // The native radios carry data-option-name, so the option is matched by
    // name rather than by DOM position — a reordered picker cannot break it.
    function isOurRadio(radio) {
      var name = radio.dataset.optionName;
      if (!name) return true; // single-option product: nothing to disambiguate
      return name.toLowerCase().trim() === optionName;
    }

    function nativeRadioFor(value) {
      var picker = getPicker();
      if (!picker) return null;
      var radios = picker.querySelectorAll('input[type="radio"]');
      for (var i = 0; i < radios.length; i++) {
        if (radios[i].value === value && isOurRadio(radios[i])) return radios[i];
      }
      return null;
    }

    function tierFor(value) {
      for (var i = 0; i < config.tiers.length; i++) {
        if (config.tiers[i].value === value) return config.tiers[i];
      }
      return null;
    }

    function renderSummary(value) {
      if (!summaryEl || !config.summaryTemplate) return;
      var tier = tierFor(value);
      if (!tier) return;

      var receivedWord = tier.total > 1 ? config.receivedWord : config.receivedWordSingular || config.receivedWord;

      var html = config.summaryTemplate;

      // A tier without a gift must not read "0 € offerts": drop the clause
      // along with the separator that precedes it, before filling the rest.
      if (tier.gifted) {
        html = html.replace('[gift_value]', escapeHtml(tier.giftValue) + ' ' + escapeHtml(config.giftedWord));
      } else {
        html = html.replace(/[\s,;·]*\[gift_value\]/, '');
      }

      // Placeholders keep the merchant in control of the wording.
      summaryEl.innerHTML = html
        .replace('[total]', '<b>' + tier.total + ' ' + escapeHtml(receivedWord) + '</b>')
        .replace('[unit]', escapeHtml(tier.unit) + ' ' + escapeHtml(config.unitWord));
    }

    function escapeHtml(str) {
      return String(str == null ? '' : str).replace(/[&<>"']/g, function (c) {
        return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
      });
    }

    function paint(value) {
      opts.forEach(function (opt) {
        var on = opt.dataset.value === value;
        opt.setAttribute('aria-checked', String(on));
        // Roving tabindex: only the checked row is in the tab order.
        opt.setAttribute('tabindex', on ? '0' : '-1');
      });
      renderSummary(value);
    }

    function select(opt, moveFocus) {
      if (opt.getAttribute('aria-disabled') === 'true') return;

      var value = opt.dataset.value;
      paint(value);
      if (moveFocus) opt.focus();

      var radio = nativeRadioFor(value);
      if (radio && !radio.checked) {
        radio.checked = true;
        // Bubbles so <variant-selects>'s delegated listener picks it up.
        radio.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }

    // Arrow keys skip sold-out rows, which are not selectable.
    function selectableFrom(index, step) {
      var count = opts.length;
      for (var i = 1; i <= count; i++) {
        var candidate = opts[(index + step * i + count * count) % count];
        if (candidate.getAttribute('aria-disabled') !== 'true') return candidate;
      }
      return null;
    }

    opts.forEach(function (opt, index) {
      opt.addEventListener('click', function () {
        select(opt, false);
      });

      opt.addEventListener('keydown', function (event) {
        var step = 0;
        if (event.key === 'ArrowDown' || event.key === 'ArrowRight') step = 1;
        if (event.key === 'ArrowUp' || event.key === 'ArrowLeft') step = -1;
        if (!step) return;

        event.preventDefault();
        var next = selectableFrom(index, step);
        if (next) select(next, true);
      });
    });

    // Initial summary for the variant rendered by Liquid.
    var checked = opts.filter(function (opt) {
      return opt.getAttribute('aria-checked') === 'true';
    })[0];

    // The default tier can be sold out — fall back to the first available one
    // so the selector never opens on an unselectable row.
    if (!checked || checked.getAttribute('aria-disabled') === 'true') {
      var fallback = opts.filter(function (opt) {
        return opt.getAttribute('aria-disabled') !== 'true';
      })[0];
      if (fallback) {
        select(fallback, false);
        return;
      }
    }

    if (checked) renderSummary(checked.dataset.value);

    // A variant change from elsewhere (back button, another control) must be
    // mirrored here, otherwise the rows and the price would disagree. Bound on
    // the document rather than the picker: the theme swaps that element out on
    // every change, taking any listener attached to it with it.
    document.addEventListener('change', function (event) {
      var target = event.target;
      if (!target || target.type !== 'radio') return;
      if (!target.closest || !target.closest('variant-selects')) return;
      if (!isOurRadio(target)) return;

      // The replacement markup arrives without our tabindex.
      silenceHiddenPicker();
      paint(target.value);
    });
  }

  function readConfig(root) {
    var fallback = { tiers: [], summaryTemplate: '', receivedWord: '', unitWord: '', giftedWord: '' };
    var script = root.parentNode && root.parentNode.querySelector('[data-rl-avp-config]');
    if (!script) return fallback;
    try {
      var parsed = JSON.parse(script.textContent);
      parsed.tiers = parsed.tiers || [];
      return parsed;
    } catch (error) {
      return fallback;
    }
  }

  function init() {
    document.querySelectorAll('[data-rl-avp]').forEach(initSelector);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
